import useMediaQuery from "@mui/material/useMediaQuery";
import Skeleton from "@mui/material/Skeleton";
import { Fragment, useLayoutEffect, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { getMetricsData } from "../../api/myDesk";
import { ROLE_TYPE } from "../../constants/constants";
import { UpdateAppSubHeaderTitle } from "../../redux/actions/UiActions";
import {
    WelcomeHeader,
    WelcomeSubtitle,
    MetricsGrid,
    MetricCard as StyledMetricCard,
    MetricAccentBar,
    MetricLabel,
    MetricBottomRow,
    MetricNumber,
    MetricIconWrapper,
    MainDataBox
} from "./styles/MyDeskStyles";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CancelIcon from "@mui/icons-material/Cancel";
import Companies from "./subViews/Company/Company";
import MyProfile from "./subViews/myProfile/MyProfile";
import MyRequests from "./subViews/MyRequests/MyRequests";
import MyRequestsCompany from "./subViews/MyRequests/MyRequestsCompany";
import { getAllCompanies } from "../../api/myCompany";
import LocalStorageService from "../../services/LocalStorageService";

function MetricCard({ color, label, value, icon }) {
    return (
        <StyledMetricCard>
            <MetricAccentBar color={color} />
            <MetricLabel>{label}</MetricLabel>
            <MetricBottomRow>
                <MetricNumber>{value}</MetricNumber>
                <MetricIconWrapper color={color}>
                    {icon}
                </MetricIconWrapper>
            </MetricBottomRow>
        </StyledMetricCard>
    );
}

function MyDesk() {
    const matchesWidth = useMediaQuery("(min-width:768px)");
    const history = useHistory();
    const dispatch = useDispatch();

    const [currentLang, setCurrentLang] = useState(LocalStorageService.getItem('language') || 'es');

    useEffect(() => {
        const handleLangChange = (e) => {
            if (e?.detail) {
                setCurrentLang(e.detail);
            } else {
                setCurrentLang(LocalStorageService.getItem('language') || 'es');
            }
        };
        window.addEventListener('languageChange', handleLangChange);
        return () => window.removeEventListener('languageChange', handleLangChange);
    }, []);

    const isEn = currentLang === 'en';
    const queryClient = useQueryClient();
    const { rolBase } = useSelector((state) => state.authReducer);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get("tab");
    const initialTab = tabParam ? parseInt(tabParam) : 1;

    const [ActiveMenu, setActiveMenu] = useState(initialTab === 0 ? 1 : initialTab);

    useEffect(() => {
        if (location.pathname === "/app/empresas" || tabParam === "3") {
            setActiveMenu(3);
        } else if (initialTab === 0) {
            history.replace("/app/perfil");
        } else {
            setActiveMenu(1);
        }
    }, [location.pathname, location.search, initialTab, tabParam, history]);

    const userData = queryClient.getQueryData(["userData"]);
    const { data: metricsData, isLoading: metricsDataIsLoading } = useQuery(
        ["myDeskMetricsData"],
        () => getMetricsData(userData.payload.citizen_id),
        {
            enabled: userData?.payload?.citizen_id !== undefined,
            cacheTime: 0,
            staleTime: 0,
        }
    );
    const { data: companiesData, isLoading: companiesLoading } = useQuery(
        ["allCompaniesData"],
        () => getAllCompanies()
    );
    const hasCompanies = rolBase === ROLE_TYPE.COMPANY || (companiesData?.payload && companiesData.payload.length > 0);

    useLayoutEffect(() => {
        dispatch(UpdateAppSubHeaderTitle("Inicio"));
    }, [dispatch]);

    if (metricsDataIsLoading) {
        return (
            <Fragment>
                <Skeleton variant="rectangular" width="60%" height={40} style={{ borderRadius: "8px", marginBottom: "12px" }} />
                <Skeleton variant="rectangular" width="80%" height={24} style={{ borderRadius: "8px", marginBottom: "32px" }} />
                <MetricsGrid>
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} style={{ position: "relative", overflow: "hidden", borderRadius: "8px", border: "1px solid #E7EEFF", backgroundColor: "#FFFFFF", padding: "24px" }}>
                            <div style={{ position: "absolute", right: 0, top: 0, height: "100%", width: "4px", backgroundColor: "#C6C5D0" }} />
                            <Skeleton variant="rectangular" width="50%" height={20} style={{ marginBottom: "16px" }} />
                            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
                                <Skeleton variant="rectangular" width={50} height={40} />
                                <Skeleton variant="circular" width={40} height={40} />
                            </div>
                        </div>
                    ))}
                </MetricsGrid>
                <div style={{ marginTop: "24px", padding: "32px", border: "1px solid rgba(198, 197, 208, 0.3)", borderRadius: "12px" }}>
                    <Skeleton variant="rectangular" height={160} style={{ borderRadius: "8px", marginBottom: "16px" }} />
                    <Skeleton variant="rectangular" height={50} style={{ borderRadius: "8px" }} />
                </div>
            </Fragment>
        );
    }

    const userName = userData?.payload?.name
        ? userData.payload.name.trim().split(' ')[0]
        : (userData?.payload?.first_name || '');

    const welcomeText = isEn
        ? (userName ? `Welcome ${userName} to UCTT` : 'Welcome to UCTT')
        : (userName ? `Bienvenido ${userName} a la UCTT` : 'Bienvenido a la UCTT');

    const welcomeSubtitle = isEn
        ? 'Manage your requests, classifications, and tourism licenses from one place with total transparency and agility.'
        : 'Administre sus solicitudes, clasificaciones y licencias turísticas desde un solo lugar con total transparencia y agilidad.';

    const isEmpresasView = location.pathname === "/app/empresas" || ActiveMenu === 3;

    return (
        <Fragment>
            {!isEmpresasView && (
                <>
                    <WelcomeHeader>{welcomeText}</WelcomeHeader>
                    <WelcomeSubtitle>
                        {welcomeSubtitle}
                    </WelcomeSubtitle>

                    <MetricsGrid>
                        <MetricCard
                            color="#1A234E"
                            label={isEn ? "Applications in process" : "Solicitudes en proceso"}
                            value={metricsData?.reqsOpen || 0}
                            icon={<PendingActionsIcon />}
                        />
                        <MetricCard
                            color="#10B981"
                            label={isEn ? "Completed applications" : "Solicitudes completadas"}
                            value={metricsData?.reqsComplete || 0}
                            icon={<CheckCircleIcon />}
                        />
                        <MetricCard
                            color="#F59E0B"
                            label={isEn ? "Uploaded documents" : "Documentos subidos"}
                            value={metricsData?.documents || 0}
                            icon={<CloudUploadIcon />}
                        />
                        <MetricCard
                            color="#B7102A"
                            label={isEn ? "Rejected applications" : "Solicitudes rechazadas"}
                            value={metricsData?.reqsRejected || 0}
                            icon={<CancelIcon />}
                        />
                    </MetricsGrid>
                </>
            )}

            {isEmpresasView ? (
                <Companies rolBase={rolBase} />
            ) : (
                <MainDataBox style={{ marginTop: "24px" }}>
                    {ActiveMenu === 0 ? (
                        <div style={{ padding: "32px" }}>
                            <MyProfile rolBase={rolBase} />
                        </div>
                    ) : (
                        rolBase === ROLE_TYPE.COMPANY ? (
                            <MyRequestsCompany rolBase={rolBase} />
                        ) : (
                            <MyRequests rolBase={rolBase} />
                        )
                    )}
                </MainDataBox>
            )}
        </Fragment>
    );
}

export default MyDesk;
